import {
    areSearchParamsEqual,
    CAMPAIGNS_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type CampaignFilterItem = { key: string; value: string };

export type CampaignsUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterCampaignId: CampaignFilterItem | null;
    filterCampaignName: CampaignFilterItem | null;
    filterCampaignTypeId: CampaignFilterItem[] | null;
    filterStatusId: CampaignFilterItem[] | null;
    filterBranchId: CampaignFilterItem[] | null;
    filterIsGeneral: CampaignFilterItem[] | null;
    filterInLineCountCalls: CampaignFilterItem | null;
    filterIsInLineCountCalls: CampaignFilterItem[] | null;
    uiFilters: Record<string, any>;
};

export type CampaignsUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterCampaignId: CampaignFilterItem | null;
    filterCampaignName: CampaignFilterItem | null;
    filterCampaignTypeId: CampaignFilterItem[] | null;
    filterStatusId: CampaignFilterItem[] | null;
    filterBranchId: CampaignFilterItem[] | null;
    filterIsGeneral: CampaignFilterItem[] | null;
    filterInLineCountCalls: CampaignFilterItem | null;
    filterIsInLineCountCalls: CampaignFilterItem[] | null;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialCampaignsUrlState(): CampaignsUrlViewState {
    return parseCampaignsUrlState(getHashSearchParams());
}

export function parseCampaignsUrlState(params: URLSearchParams): CampaignsUrlViewState {
    params = unwrapEncodedSearchParams(params, CAMPAIGNS_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const archiveTableToggle = !parseBooleanParam(params.get('archived'), false);

    const campaignId = params.get('campaignId');
    const name = params.get('name');
    const inLineCountCalls = params.get('inLineCountCalls');
    const campaignTypeId = getMultiParams(params, 'campaignTypeId').map(toNumberValue);
    const statusId = getMultiParams(params, 'statusId').map(toNumberValue);
    const branchId = getMultiParams(params, 'branchId').map(toNumberValue);
    const isGeneralTrunk = getMultiParams(params, 'isGeneralTrunk').map(toBooleanValue);
    const isInLineCountCalls = getMultiParams(params, 'isInLineCountCalls').map(toTrueFalseString);

    if (campaignId) uiFilters.campaignId = campaignId;
    if (name) uiFilters.name = name;
    if (inLineCountCalls) uiFilters.inLineCountCalls = inLineCountCalls;
    if (campaignTypeId.length) uiFilters.campaignTypeId = campaignTypeId;
    if (statusId.length) uiFilters.statusId = statusId;
    if (branchId.length) uiFilters.branchId = branchId;
    if (isGeneralTrunk.length) uiFilters.isGeneralTrunk = isGeneralTrunk;
    if (isInLineCountCalls.length) uiFilters.isInLineCountCalls = isInLineCountCalls;

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        archiveTableToggle,
        filterCampaignId: toFilterItem('CampaignId', campaignId),
        filterCampaignName: toFilterItem('CampaignName', name),
        filterInLineCountCalls: toFilterItem('InLineCountCalls', inLineCountCalls),
        filterCampaignTypeId: toFilterList('CampaignTypeId', campaignTypeId),
        filterStatusId: toFilterList('StatusId', statusId),
        filterBranchId: toFilterList('BranchId', branchId),
        filterIsGeneral: toFilterList('IsGeneralTrunk', isGeneralTrunk),
        filterIsInLineCountCalls: toFilterList('IsInLineCountCalls', isInLineCountCalls),
        uiFilters,
    };
}

export function buildCampaignsSearchParams(state: CampaignsUrlWriteState): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainCampaignsSearchParams(state), CAMPAIGNS_ENCODED_QUERY_KEY);
}

function buildPlainCampaignsSearchParams(state: CampaignsUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'CampaignId' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }
    if (!state.archiveTableToggle) {
        params.set('archived', '1');
    }

    setScalar(params, 'campaignId', state.filterCampaignId?.value);
    setScalar(params, 'name', state.filterCampaignName?.value);
    setScalar(params, 'inLineCountCalls', state.filterInLineCountCalls?.value);
    setMulti(params, 'campaignTypeId', state.filterCampaignTypeId);
    setMulti(params, 'statusId', state.filterStatusId);
    setMulti(params, 'branchId', state.filterBranchId);
    setMulti(params, 'isGeneralTrunk', state.filterIsGeneral);
    setMulti(params, 'isInLineCountCalls', state.filterIsInLineCountCalls);

    return params;
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function setMulti(params: URLSearchParams, key: string, items?: CampaignFilterItem[] | null) {
    items?.forEach((item) => {
        if (item?.value !== undefined && item?.value !== null && item.value !== '') {
            params.append(key, normalizeBooleanParam(item.value));
        }
    });
}

function getMultiParams(params: URLSearchParams, key: string): string[] {
    const values: string[] = [];
    params.getAll(key).forEach((value) => {
        value.split(',').forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) values.push(trimmed);
        });
    });
    return values;
}

function toFilterItem(key: string, value?: string | null): CampaignFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toFilterList(key: string, values?: Array<string | number | boolean>): CampaignFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value: String(value) }));
}

function toBooleanValue(value: string): boolean {
    return value === '1' || value === 'true';
}

function toTrueFalseString(value: string): string {
    if (value === '1' || value === 'true') return 'true';
    if (value === '0' || value === 'false') return 'false';
    return value;
}

function toNumberValue(value: string): number | string {
    if (/^-?\d+$/.test(value)) {
        const parsed = Number(value);
        if (Number.isSafeInteger(parsed)) {
            return parsed;
        }
    }
    return value;
}

function normalizeBooleanParam(value: string): string {
    if (value === 'true') return '1';
    if (value === 'false') return '0';
    return value;
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}
